/**
 * History import (prototype histModal / hiRead / hiApply; contracts routes.history.ts). The usage report's pipeline:
 *
 *   POST /tasks/history-import/sign   an upload slot (its upload_id is the import id every later call takes)
 *   PUT  <slot url>                   the browser sends the sheet straight to storage
 *   POST /tasks/history-import        dry_run=true: the worker reads it (status parsing → staged | failed)
 *   GET  /tasks/history-import/{id}   the per-row preview: Ready, or why not
 *   POST /tasks/history-import        dry_run=false: every ready row written in one transaction
 *
 * Every write takes its own Idempotency-Key: the API answers 409 "Use a new key" when one key comes back with a
 * different body, so the preview and the import never share one, and a re-picked file starts over with new ones.
 * The dry run is only ever sent after the upload has resolved, so "The file has not arrived yet" (422) cannot come
 * from a race; if it comes anyway its own words are shown, and reading again is just another dry run (which since
 * 92cf27f also re-queues a read whose job was lost).
 */
import type { HistoryImport, HistoryImportDetail, HistoryImportKind } from '@amplispot/contracts';
import { ApiFailure, call, newIdempotencyKey } from '../api';
import { uploadToSlot } from './usage';

/** Sign a slot and send the sheet to it. Answers the import id. */
export async function sendHistorySheet(kind: HistoryImportKind, f: File): Promise<string> {
  const content_type = f.type || 'application/octet-stream';
  const slot = await call('historyImport.sign', {
    body: { kind, filename: f.name, bytes: f.size, content_type },
    idempotencyKey: newIdempotencyKey(),
  });
  await uploadToSlot(slot, f, content_type);
  return slot.upload_id;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Ask for the dry run and poll until the worker has read the sheet: staged (the rows), or failed (parse_error says
 * why: an empty file, not a table, over 5 000 rows). `alive` going false (the file was re-picked, the kind switched,
 * the pop-up closed) stops the polling and answers null.
 */
export async function previewHistory(id: string, alive: () => boolean): Promise<HistoryImportDetail | null> {
  await call('historyImport.run', { body: { import_id: id, dry_run: true }, idempotencyKey: newIdempotencyKey() });
  for (let i = 0; i < 120; i++) {
    if (!alive()) return null;
    const d = await call('historyImport.get', { params: { id } });
    if (d.status !== 'parsing' && d.status !== 'awaiting_file') return d;
    await sleep(i < 10 ? 500 : 1500);
  }
  throw new Error('Reading the file is taking longer than usual. Try again in a minute.');
}

/** Write every ready row. 409 unless staged (never twice); 422 when no row is ready. */
export const applyHistory = (id: string): Promise<HistoryImport> =>
  call('historyImport.run', { body: { import_id: id, dry_run: false }, idempotencyKey: newIdempotencyKey() });

/** The top line of a 422 from the contract's schema check (apps/api contract-route.ts); the field says what is wrong. */
const SCHEMA_REFUSAL = 'Some of what was sent is not valid. Fix the fields listed and try again.';

/**
 * A refusal in plain words. A schema 422 carries only the generic line on top, so its field message is shown ("The
 * file is over 10 MB…"). A handler's 422 carries the whole sentence on top and a short label on the field ("The file
 * has not arrived yet. Wait for the upload to finish, then try again." / "No file yet"), so the top line is shown.
 */
export function historyFailText(e: unknown): string {
  if (e instanceof ApiFailure) {
    const field = e.body?.error.field_errors?.[0]?.message;
    return field && e.message === SCHEMA_REFUSAL ? field : e.message;
  }
  return e instanceof Error ? e.message : 'Something went wrong. Try again.';
}
